import React, { useState, useContext } from 'react';
import { Input, InputNumber, Form, Button, Row, Col, Collapse, Card, Space, Popconfirm, message, Select, ConfigProvider, Modal } from 'antd';
import { FileOutlined, CheckCircleOutlined, WarningOutlined, LineHeightOutlined, PoweroffOutlined } from '@ant-design/icons';
import AppLayout from "./components/Layout";
import { LanguageContext } from "./LanguageContext";

const importAllIcons = (requireContext) => {
    let icons = {};
    requireContext.keys().forEach((key) => {
      const match = key.match(/\.\/(\d+)\.svg$/);
      if (match) {
        const iconKey = match[1];
        const importedIcon = requireContext(key);
        // Eğer modül default olarak export edilmişse onu, aksi halde direkt modülü alıyoruz.
        icons[iconKey] = importedIcon.default || importedIcon;
      }
    });
    return icons;
  };
  
  // Eğer EditableJson componenti 'src/components' altında ise, ikonlar klasörünün yolu '../ikonlar' şeklinde olmalıdır.
  // Durumunuza göre yolu ' ./ikonlar' şeklinde de düzenleyebilirsiniz.
  const icons = importAllIcons(require.context('./ikonlar', false, /\.svg$/));

  
const EditableJson = () => {
  const [iconModalVisible, setIconModalVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentOutputIndex, setCurrentOutputIndex] = useState(null);
  const [currentInputIndex, setCurrentInputIndex] = useState(null);
  const [currentVoltageIndex, setCurrentVoltageIndex] = useState(null);
  const [currentFloatIndex, setCurrentFloatIndex] = useState(null);
  const { language, translations } = useContext(LanguageContext);

  const handleIconSelect = (iconNumber) => {
    if (currentOutputIndex !== null) {
      const updatedOutputs = [...outputs];
      updatedOutputs[currentOutputIndex].icon = iconNumber;
      setOutputs(updatedOutputs);
      setIconModalVisible(false);
      setCurrentOutputIndex(null);
    };
    if (currentFloatIndex !== null) {
        const updatedFloats = [...floats];
        updatedFloats[currentFloatIndex].icon = iconNumber;
        setFloats(updatedFloats);
        setIconModalVisible(false);
        setCurrentFloatIndex(null);
    };
    if (currentVoltageIndex !== null) {
        const updatedVoltages = [...voltages];
        updatedVoltages[currentVoltageIndex].icon = iconNumber;
        setVoltages(updatedVoltages);
        setIconModalVisible(false);
        setCurrentVoltageIndex(null);
    };
    if (currentInputIndex !== null) {
        const updatedInputs = [...inputs];
        updatedInputs[currentInputIndex].icon = iconNumber;
        setInputs(updatedInputs);
        setIconModalVisible(false);
        setCurrentInputIndex(null);
    };
  };

  const [selectedIcon, setSelectedIcon] = useState(null);
  const [hoveredIcon, setHoveredIcon] = useState(null);

  const handleMouseEnter = (icon) => {
    setHoveredIcon(icon);
  };

  const handleMouseLeave = () => {
    setHoveredIcon(null);
  };

  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);

  const [formValues, setFormValues] = useState({
    first_row_capacity: 4,
    second_row_capacity: 4
  });

  

  // Karanlık mod açma-kapama fonksiyonu
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Karanlık mod için Ant Design tema ayarları
  const darkTheme = {
    token: {
      colorBgBase: '#141414', // Temel arka plan rengi
      colorTextBase: '#ffffff', // Metin rengi
      colorLink: '#1890ff', // Link rengi
      colorPrimary: '#1890ff', // Birincil renk
    },
  };

  const allFloatsPins = ["B6", "B7", "C1", "C2", "C3"];
  const allSocketPins = ["A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8",
         "B1", "B8",
         "C6", "C7", "C8",
         "D0", "D1", "D2"];
  const allInputPins = ["B4", "B3", "B2", "B1"];
  const allVoltagesPins = ["00",
         "C1", "C2", "C3", "C4"];

  const allOutputPins = ["A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8",
         "B1", "B8",
         "C6", "C7", "C8",
         "D0", "D1", "D2"];
  
  const [outputs, setOutputs] = useState([
    {
      socketpin: "A1",
      type: "enabled",
      currentlimit: 10000,
      currentgreenlimit: 100,
      page: 1,
      row: 2,
      column: 1,
      icon: 1,
      nameTR: "Buzdolabı",
      nameGB: "Fridge",
      nameDE: "Kühlschrank",
      cloneActive: false, 
    }
  ]);

  const handleCloneOutput = (index) => {
    const updatedOutputs = [...outputs];
    if (!updatedOutputs[index].cloneActive) {
      updatedOutputs[index].cloneActive = true;
      updatedOutputs[index].page2 = updatedOutputs[index].page + 1; // Örneğin mevcut sayfaya göre
      updatedOutputs[index].row2 = updatedOutputs[index].row;
      updatedOutputs[index].column2 = updatedOutputs[index].column;
    }
    setOutputs(updatedOutputs);
  };

  const handleRemoveCloneOutput = (index) => {
    const updatedOutputs = [...outputs];
    if (updatedOutputs[index].cloneActive) {
      updatedOutputs[index].cloneActive = false;
      // Klon alanlarını silmek için:
      delete updatedOutputs[index].page2;
      delete updatedOutputs[index].row2;
      delete updatedOutputs[index].column2;
    }
    setOutputs(updatedOutputs);
  };
  

  const handleAddOutput = () => {
    const newOutput = {
      socketpin: "A1",
      type: "enabled",
      currentlimit: 10000,
      currentgreenlimit: 100,
      page: 1,
      row: 2,
      column: 1,
      icon: 1,
      nameTR: "Yeni Çıkış TR",
      nameGB: "New Output GB",
      nameDE: "Neuer Ausgang DE"
    };
    setOutputs([...outputs, newOutput]);
  };

  const handleDeleteOutput = (index) => {
    const newOutputs = [...outputs];
    newOutputs.splice(index, 1);
    setOutputs(newOutputs);
  };

  const handleAddFloat =() =>{
    const newFloat ={
      socketpin: "B6",
      enable: "true",
      type: "0-190",
      purpose: "water",
      warn_percent: 20,
      error_percent: 0,
      lock_pin: "A2",
      page: 1,
      row: 1,
      column: 3,
      icon: 0,
      nameTR: "Temiz Su",
      nameGB: "Clean Water",
      nameDE: "Sauberes Wasser"
    };
    setFloats([...floats, newFloat])
  }

  const [floats, setFloats] = useState([
    {
      socketpin: "B6",
      enable: "true",
      type: "0-190",
      purpose: "water",
      warn_percent: 20,
      error_percent: 0,
      lock_pin: "A2",
      page: 1,
      row: 1,
      column: 3,
      icon: 0,
      nameTR: "Temiz Su",
      nameGB: "Clean Water",
      nameDE: "Sauberes Wasser",
      cloneActive: false,
    }
  ]);

  const handleCloneFloat = (index) => {
    const updatedFloats = [...floats];
    if (!updatedFloats[index].cloneActive) {
      updatedFloats[index].cloneActive = true;
      updatedFloats[index].page2 = updatedFloats[index].page + 1; // Örneğin mevcut sayfaya göre
      updatedFloats[index].row2 = updatedFloats[index].row;
      updatedFloats[index].column2 = updatedFloats[index].column;
    }
    setFloats(updatedFloats);
  };

  const handleRemoveCloneFloat = (index) => {
    const updatedFloats = [...floats];
    if (updatedFloats[index].cloneActive) {
      updatedFloats[index].cloneActive = false;
      // Klon alanlarını silmek için:
      delete updatedFloats[index].page2;
      delete updatedFloats[index].row2;
      delete updatedFloats[index].column2;
    }
    setFloats(updatedFloats);
  };


  const handleAddVoltage =() =>{
    const newVoltage ={
      socketpin : "00",
      enable : "true",
      page: 1,
      row: 1,
      column: 1,
      icon: 30,
      nameTR: "Servis Aküsü",
      nameGB: "Servicebatterie",
      nameDE: "Servicebatterie"
    };
    setVoltages([...voltages, newVoltage])
  }

  const handleDeleteFloat =(index) => {
    const newFloats = [...floats];
    newFloats.splice(index,1);
    setFloats(newFloats);
  };

  const [voltages, setVoltages] = useState([
    {
      socketpin: "00",
      enable: "true",
      page: 1,
      row: 1,
      column: 1,
      icon: 30,
      nameTR: "Servis Aküsü",
      nameGB: "Servicebatterie",
      nameDE: "Servicebatterie",
      cloneActive: false,
    }
  ]);

  const handleCloneVoltage = (index) => {
    const updatedVoltages = [...voltages];
    if (!updatedVoltages[index].cloneActive) {
      updatedVoltages[index].cloneActive = true;
      updatedVoltages[index].page2 = updatedVoltages[index].page + 1; // Örneğin mevcut sayfaya göre
      updatedVoltages[index].row2 = updatedVoltages[index].row;
      updatedVoltages[index].column2 = updatedVoltages[index].column;
    }
    setVoltages(updatedVoltages);
  };

  const handleRemoveCloneVoltage = (index) => {
    const updatedVoltages = [...voltages];
    if (updatedVoltages[index].cloneActive) {
      updatedVoltages[index].cloneActive = false;
      // Klon alanlarını silmek için:
      delete updatedVoltages[index].page2;
      delete updatedVoltages[index].row2;
      delete updatedVoltages[index].column2;
    }
    setVoltages(updatedVoltages);
  };

  const handleDeleteVoltages =(index) => {
    const newVoltages = [...inputs];
    newVoltages.splice(index,1);
    setVoltages(newVoltages);
  };

  const handleAddRelay =() => {
    const newRelay ={
      enable: "true",
      short_circuit_socketpin: "C6",
      open_circuit_socketpin: "B8",
      short_circuit_voltage: 13200,
      open_circuit_voltage: 12500,
      manualoverride_duration: 75
    };
    setRelays([...relays, newRelay]);
  };

  const [relays, setRelays] = useState([
    {
      enable: "true",
      short_circuit_socketpin: "C6",
      open_circuit_socketpin: "B8",
      short_circuit_voltage: 13200,
      open_circuit_voltage: 12500,
      manualoverride_duration: 75
    }
  ]);

  const handleAddSettings =() => {
    const newSetting ={
      force_shut_down_voltage: 10500,
      optional_down_voltage: 11000,
      start_warn_voltage: 11500,
      recover_voltage: 12000,
      force_shut_down_time: 60
    };
    setSettings([...settings, newSetting]);
  };

  const [settings, setSettings] = useState([
    {
      force_shut_down_voltage: 10500,
      optional_down_voltage: 11000,
      start_warn_voltage: 11500,
      recover_voltage: 12000,
      force_shut_down_time: 60
    }
  ]);

  const handleAddInput =() => {
    const newInput ={
      socketpin: "B3",
      enable: "true",
      vavienoutput: "A3",
      page: 3,
      row: 1,
      column: 2,
      icon: 6,
      nameTR: "Dış Aydınlatma",
      nameGB: "Outdoor Lighting",
      nameDE: "Außenbeleuchtung"
    };
    setInputs([...inputs, newInput]);
  };

  const [inputs, setInputs] = useState([
    {
      socketpin: "B3",
      enable: "true",
      vavienoutput: "A3",
      page: 3,
      row: 1,
      column: 2,
      icon: 6,
      nameTR: "Dış Aydınlatma",
      nameGB: "Outdoor Lighting",
      nameDE: "Außenbeleuchtung",
      cloneActive: false,
    }
  ]);

  const handleCloneInput = (index) => {
    const updatedInputs = [...inputs];
    if (!updatedInputs[index].cloneActive) {
      updatedInputs[index].cloneActive = true;
      updatedInputs[index].page2 = updatedInputs[index].page + 1; // Örneğin mevcut sayfaya göre
      updatedInputs[index].row2 = updatedInputs[index].row;
      updatedInputs[index].column2 = updatedInputs[index].column;
    }
    setInputs(updatedInputs);
  };

  const handleRemoveCloneInput = (index) => {
    const updatedInputs = [...inputs];
    if (updatedInputs[index].cloneActive) {
      updatedInputs[index].cloneActive = false;
      // Klon alanlarını silmek için:
      delete updatedInputs[index].page2;
      delete updatedInputs[index].row2;
      delete updatedInputs[index].column2;
    }
    setInputs(updatedInputs);
  };

  const handleDeleteInput =(index) => {
    const newInputs = [...inputs];
    newInputs.splice(index,1);
    setInputs(newInputs);
  };

  const [pages, setPages] = useState([
    { no: 1, first_row_capacity: 4, second_row_capacity: 4, name: "-" },
    { no: 2, first_row_capacity: 4, second_row_capacity: 4, name: "-" },
    { no: 3, first_row_capacity: 4, second_row_capacity: 3, name: "-" }
  ]);
  

  const handleChange = (index, field, value, setState, state, isPageField = false) => {
    if (field === 'page' && !isPageField) {
      const maxPage = pages.length;
      if (value > maxPage) {
        message.error(`Sayfa numarası maksimum sayfa sayısını aşamaz (${maxPage}).`);
        return;
      }
    }
  
    // Değeri integer olup olmadığını kontrol et
    const parsedValue = /^\d+$/.test(value) ? parseInt(value, 10) : value;
  
    const updatedState = [...state];
    updatedState[index][field] = parsedValue; // Integer ise number, değilse string olarak kaydet
    setState(updatedState);
  };
  
  const handleAddPage = () => {
    if (pages.length >= 10) {
      message.warning('Maksimum 10 sayfa eklenebilir.');
      return;
    }
    
    // Burada formdaki input değerlerini alıyoruz (örneğin state ile)
    const firstRowCapacity = formValues.first_row_capacity;
    const secondRowCapacity = formValues.second_row_capacity;
  
    const newPage = {
      no: pages.length + 1,
      first_row_capacity: firstRowCapacity,
      second_row_capacity: secondRowCapacity
    };
  
    setPages([...pages, newPage]);
  };
  

  const handleDeletePage = (index) => {
    const newPages = [...pages];
    newPages.splice(index, 1); // Sayfayı sil
  
    // Sayfa numaralarını güncelle
    const renumberedPages = newPages.map((page, idx) => ({
      ...page,
      no: idx + 1, // Yeni numaraları sıfırdan başlayarak ayarla
    }));
  
    setPages(renumberedPages); // Yeni listeyi kaydet
  };
  

  const translateText = async (text, from, to) => {
    // Çeviri API'sine istek at
    const response = await fetch(
      `https://api.mymemory.translated.net/get?q=${text}&langpair=${from}|${to}`
    );
    const data = await response.json();
    return data.responseData.translatedText;
  };

  const handleVoltageChangeForFloat = async (index, field, value) => {
    // Türkçe (TR) alanına yazılan değeri güncelle
    setFloats((prev) => {
      const uptadeFloats = [...prev];
      uptadeFloats[index][field] = value;

      if (field === 'nameTR') {
        // Türkçe alanı güncellerken İngilizce ve Almanca çevirilerini yap
        const enTranslation = translateText(value, 'tr', 'en');
        const deTranslation = translateText(value, 'tr', 'de');

        enTranslation.then((en) => {
          deTranslation.then((de) => {
            uptadeFloats[index].nameGB = en;
            uptadeFloats[index].nameDE = de;
            setFloats(uptadeFloats);
          });
        });
      }

      return uptadeFloats;
    });
  };

  const handleVoltageChangeForInput = async (index, field, value) => {
    // Türkçe (TR) alanına yazılan değeri güncelle
    setVoltages((prev) => {
      const updatedVoltages = [...prev];
      updatedVoltages[index][field] = value;

      if (field === 'nameTR') {
        // Türkçe alanı güncellerken İngilizce ve Almanca çevirilerini yap
        const enTranslation = translateText(value, 'tr', 'en');
        const deTranslation = translateText(value, 'tr', 'de');

        enTranslation.then((en) => {
          deTranslation.then((de) => {
            updatedVoltages[index].nameGB = en;
            updatedVoltages[index].nameDE = de;
            setVoltages(updatedVoltages);
          });
        });
      }

      return updatedVoltages;
    });
  };

  const handleInputChangeForInput = async (index, field, value) => {
    // Türkçe (TR) alanına yazılan değeri güncelle
    setInputs((prev) => {
      const updatedInputs = [...prev];
      updatedInputs[index][field] = value;

      if (field === 'nameTR') {
        // Türkçe alanı güncellerken İngilizce ve Almanca çevirilerini yap
        const enTranslation = translateText(value, 'tr', 'en');
        const deTranslation = translateText(value, 'tr', 'de');

        enTranslation.then((en) => {
          deTranslation.then((de) => {
            updatedInputs[index].nameGB = en;
            updatedInputs[index].nameDE = de;
            setInputs(updatedInputs);
          });
        });
      }

      return updatedInputs;
    });
  };

  let debounceTimer; // Global veya dış scope'ta bir değişken tanımlayın

const handleInputChange = (index, field, value) => {
  // Alanı güncelle
  setOutputs((prev) => {
    const updatedOutputs = [...prev];
    updatedOutputs[index][field] = value;
    return updatedOutputs;
  });

  // Eğer Türkçe alanı değiştiyse, çeviriyi gecikmeli yap
  if (field === 'nameTR') {
    // Daha önceki timer varsa temizle
    clearTimeout(debounceTimer);

    // Yeni bir timer başlat
    debounceTimer = setTimeout(() => {
      // Çeviri işlemleri
      const enTranslation = translateText(value, 'tr', 'en');
      const deTranslation = translateText(value, 'tr', 'de');

      Promise.all([enTranslation, deTranslation]).then(([en, de]) => {
        setOutputs((prev) => {
          const updatedOutputs = [...prev];
          updatedOutputs[index].nameGB = en;
          updatedOutputs[index].nameDE = de;
          return updatedOutputs;
        });
      });
    }, 500); // 500ms bekleme süresi
  }
};


let customerInputValues = {
  companyName: "TestCompany",
  caravanType: "TypeA",
  description: "Test açıklama",
  customerNameSurname: "Ahmet Yılmaz",
  customerPhone: "5551234567",
  customerCountry: "TR"
};

  const handleSaveChanges = () => {
    const cleanedOutputs = outputs.map(output => {
      const { cloneActive, ...rest } = output;
      if (!cloneActive) {
        // Klon aktif değilse, clone alanlarını da kaldırıyoruz.
        const { page2, row2, column2, ...cleaned } = rest;
        return cleaned;
      }
      return rest;
    });

    const cleanedInputs = inputs.map(input => {
      const { cloneActive, ...rest } = input;
      if (!cloneActive) {
        // Klon aktif değilse, clone alanlarını da kaldırıyoruz.
        const { page2, row2, column2, ...cleaned } = rest;
        return cleaned;
      }
      return rest;
    });

    const cleanedVoltages = voltages.map(voltage => {
      const { cloneActive, ...rest } = voltage;
      if (!cloneActive) {
        // Klon aktif değilse, clone alanlarını da kaldırıyoruz.
        const { page2, row2, column2, ...cleaned } = rest;
        return cleaned;
      }
      return rest;
    });

    const cleanedFloats = floats.map(float => {
      const { cloneActive, ...rest } = float;
      if (!cloneActive) {
        // Klon aktif değilse, clone alanlarını da kaldırıyoruz.
        const { page2, row2, column2, ...cleaned } = rest;
        return cleaned;
      }
      return rest;
    });
    
    const existingFloatPins = cleanedFloats.map(float => float.socketpin);
    const missingFloatPins = allFloatsPins.filter(pin => !existingFloatPins.includes(pin));
    const missingFloatData = missingFloatPins.map(pin => ({
      socketpin: pin,
      enable: "false"
    }));

    const existingVoltagePins = cleanedVoltages.map(voltage => voltage.socketpin);
    const missingVoltagePins = allVoltagesPins.filter(pin => !existingVoltagePins.includes(pin));
    const missingVoltageData = missingVoltagePins.map(pin =>({
      socketpin: pin,
      enable: "false"
    }));


    const existingInputPins = cleanedInputs.map(input =>input.socketpin);
    const missinInputPins = allInputPins.filter(pin => !existingInputPins.includes(pin));
    const missingInputData = missinInputPins.map(pin => ({
      socketpin: pin,
      enable: "false"
    }));

    const existingOutputPins = cleanedOutputs.map(output => output.socketpin);
    const missingOutputPins = allOutputPins.filter(pin => !existingOutputPins.includes(pin));
    const missingOutputData = missingOutputPins.map(pin => ({
      socketpin: pin,
      enable: "false"
    }));

    const updatedOutpus = [...outputs, ...missingOutputData];
    const updateVoltages = [...voltages, ...missingVoltageData];
    const updatedFloats = [...floats, ...missingFloatData];
    const updatedInputs = [...inputs, ...missingInputData];
    const filteredPages = pages.map(({ name, ...rest }) => rest);

    const jsonData = {
      outputs: [...cleanedOutputs, ...missingOutputData],
      pages: filteredPages,
      inputs: [...cleanedInputs, ...missingInputData],
      voltages: [...cleanedVoltages, ...missingInputData],
      floats: [...cleanedFloats, ...missingInputData],
      relays: relays[0], 
      settings: settings[0]
    };

    // JSON verisini dosya olarak indir
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'config.json'; // Dosya adı
    link.click();

    
    const escapedConfig = JSON.stringify(jsonData); // Escape edilmiş JSON string
    const formData = new FormData();
    // Aşağıdaki satırlarda sabit değerler yerine global değişken customerInputValues üzerinden değer okunmalı.
    formData.append("CompanyName", customerInputValues.companyName);
    formData.append("CaravanType", customerInputValues.caravanType);
    formData.append("Description", customerInputValues.description);
    formData.append("CustomerNameSurname", customerInputValues.customerNameSurname);
    formData.append("CustomerPhone", customerInputValues.customerPhone);
    formData.append("CustomerCountry", customerInputValues.customerCountry);
    formData.append("Config", escapedConfig); // 👈 Escape edilmiş string olarak gönderiyoruz
  
    fetch("https://json-configrator-api.root.myoctoopidev.com/Customer/Create", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        companyName: customerInputValues.companyName,
        caravanType: customerInputValues.caravanType,
        description: customerInputValues.description,
        customerNameSurname: customerInputValues.customerNameSurname,
        customerPhone: customerInputValues.customerPhone,
        customerCountry: customerInputValues.customerCountry,
        config: JSON.stringify(jsonData) // 👈 DİKKAT: Escape edilmiş string olarak gidiyor
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log("Başarılı:", data);
    })
    .catch(error => {
      console.error("Hata:", error);
    });

  };

  const openCustomerModal = () => {
    setIsModalVisible(true);
  };

  // Modal form gönderildiğinde global değişkeni güncelle ve handleSaveChanges’i tetikle
  const handleModalOk = (values) => {
    // Modal ile gelen değerler boş bile olsa customerInputValues güncelleniyor.
    customerInputValues = { ...customerInputValues, ...values };
    setIsModalVisible(false);
    // Form gönderim işlemi sonrasında handleSaveChanges fonksiyonu çağrılıyor.
    handleSaveChanges();
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };
 

  return (
    <AppLayout>
          <ConfigProvider theme={isDarkMode ? darkTheme : undefined}>
          <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '110vh',
      }}
    >

    {/* <Button
        type="primary"
        icon={isDarkMode ? <CheckCircleOutlined /> : <PoweroffOutlined />}
        onClick={toggleDarkMode}
        style={{ marginBottom: '20px' }} // Buton ile aşağıdaki div arasında boşluk bırakır
      >
        {isDarkMode ? "Light Mode" : "Dark Mode"}
      </Button> */}
          <div style={{ width: '700px', height: '800px', padding: '8px', fontSize: '10px' }}>
          <Card title={translations[language].layoutTitle} bordered={false}>
            <Form layout="vertical">
              <Collapse defaultActiveKey={['1', '2','3']} accordion>

              {/* Pages Section */}
              <Collapse.Panel header={`${translations[language].pages}`} key="2">
                  {pages.map((page, index) => (
                    <Card
                      key={index}
                      title={`${translations[language].pages} ${page.no}`}
                      bordered
                      style={{ marginBottom: '20px', borderRadius: '8px' }}
                      extra={
                        <Popconfirm
                          title={translations[language].deleteConfirm}
                          onConfirm={() => handleDeletePage(index)}
                          okText={translations[language].yes}
                          cancelText={translations[language].no}
                        >
                          <Button
                            type="link"
                            danger
                            style={{ fontWeight: 'bold', color: '#ff4d4f', padding: '0 10px' }}
                          >
                            {translations[language].delete}
                          </Button>
                        </Popconfirm>
                      }
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item label={translations[language].firstRowCapacity}>
                            <Select
                              value={page.first_row_capacity}
                              onChange={(value) => handleChange(index, 'first_row_capacity', value, setPages, pages, true)}
                              style={{ width: '100%' }}
                            >
                              {[0,1, 2, 3, 4, 5].map((val) => (
                                <Select.Option key={val} value={val}>
                                  {val}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item label={translations[language].secondRowCapacity}>
                            <Select
                              value={page.second_row_capacity}
                              onChange={(value) => handleChange(index, 'second_row_capacity', value, setPages, pages, true)}
                              style={{ width: '100%' }}
                            >
                              {[-1,0,1, 2, 3, 4, 5].map((val) => (
                                <Select.Option key={val} value={val}>
                                  {val}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={24}>
                          <Form.Item label={translations[language].pageAlias}>
                            <Input
                              value={page.name}
                              onChange={(e) =>
                                handleChange(index, 'name', e.target.value, setPages, pages, true)
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={handleAddPage}>
                  {translations[language].addPage}
                  </Button>
                
                </Collapse.Panel>

                {/* Outputs Section */}
                <Collapse.Panel header={`${translations[language].outputs}`} key="1">
                  {outputs.map((output, index) => (
                    <Card
                      key={index}
                      title={`${translations[language].outputName} ${index + 1}`}
                      bordered
                      style={{ marginBottom: '20px', borderRadius: '8px' }}
                      extra={
                        <Popconfirm
                          title={translations[language].deleteConfirm}
                          onConfirm={() => handleDeleteOutput(index)}
                          okText={translations[language].yes}
                          cancelText={translations[language].no}
                        >
                          <Button
                            type="link"
                            danger
                            style={{ fontWeight: 'bold', color: '#ff4d4f', padding: '0 10px' }}
                          >
                            {translations[language].delete}
                          </Button>
                        </Popconfirm>
                      }
                    >
                      <Row gutter={16}>
                      <Col span={8}>
                            <Form.Item label={translations[language].outputPin}>
                            <Select
                              value={output.socketpin}
                              onChange={(value) => handleChange(index, 'socketpin', value, setOutputs, outputs)}
                            >
                              {["A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8", "B1", "B8", "C6", "C7", "C8", "D0", "D1", "D2"].map((pin) => {
                                const isDisabled = outputs.some((o) => o.socketpin === pin); // Pin zaten seçilmişse disabled yap
                                return (
                                  <Select.Option key={pin} value={pin} disabled={isDisabled}>
                                    {pin}
                                  </Select.Option>
                                );
                              })}
                            </Select>

                            </Form.Item>
                          </Col>
                        <Col span={8}>
                        <Form.Item label={translations[language].type}>
                          <Select
                            value={output.type}
                            onChange={(value) => handleChange(index, 'type', value, setOutputs, outputs)}
                          >
                            <Select.Option value="disabled">{translations[language].disabled}</Select.Option>
                            <Select.Option value="enabled">{translations[language].enabled}</Select.Option>
                            <Select.Option value="fix">{translations[language].Fix}</Select.Option>
                            <Select.Option value="relay">{translations[language].relay}</Select.Option>
                          </Select>
                        </Form.Item>
                        </Col>
                        <Col span={8}>
  <Form.Item 
    label={translations[language].currentLimit}
    name={`currentlimit_${index}`} // Form.Item için name prop'u eklemek iyi bir uygulamadır
    rules={[
      {
        validator: (_, value) => {
          if (value < 0) {
            return Promise.reject(new Error('Negatif değer giremezsiniz'));
          }
          return Promise.resolve();
        }
      }
    ]}
    initialValue={10000}
  >
    <InputNumber
      min={0} // Negatif değerleri engeller
      defaultValue={100}
      value={output.currentlimit ?? 10000}
      onChange={(value) => handleChange(index, 'currentlimit', value, setOutputs, outputs)}
      style={{ width: '100%' }}
    />
  </Form.Item>
</Col>
                      </Row>
                      <Row gutter={16}>
                      <Col span={8}>
                      <Form.Item label={translations[language].page}>
                        <Select
                          value={output.page}
                          onChange={(value) => handleChange(index, 'page', value, setOutputs, outputs)}
                          style={{ width: '100%' }}
                        >
                          {pages.map((page) => (
                            <Select.Option key={page.no} value={page.no}>
                              {`${translations[language].page} ${page.no} ${page.name ? `- ${page.name}` : ''}`}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                        </Col>
                      <Col span={8}>
                      <Form.Item label={translations[language].column}>
                        <Select
                          value={output.column}
                          onChange={(value) => handleChange(index, 'column', value, setOutputs, outputs)}
                          style={{ width: '100%' }}
                        >
                          {[0, 1, 2, 3, 4, 5].map((value) => (
                            <Select.Option key={value} value={value}>
                              {value}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      </Col>

                        <Col span={8}>
                        <Form.Item label={translations[language].row}>
                          <Select
                            value={output.row}
                            onChange={(value) => handleChange(index, 'row', value, setOutputs, outputs)}
                            style={{ width: '100%' }}
                          >
                            <Select.Option value={0}>0</Select.Option>
                            <Select.Option value={1}>1</Select.Option>
                            <Select.Option value={2}>2</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={24}>
      {output.cloneActive ? (
          <Button type="default" style={{ width: '100%' }} onClick={() => handleRemoveCloneOutput(index)}>
            {translations[language].deleteClone}
          </Button>
        ) : (
          <Button type="default" style={{ width: '100%' }} onClick={() => handleCloneOutput(index)}>
            {translations[language].clone}
          </Button>
        )}
      </Col>
                      </Row>
                      {/* Eğer klon seçeneği aktifse, ek seçim alanlarını göster */}
    {output.cloneActive && (
      <Row gutter={16} style={{ marginTop: '10px' }}>
        <Col span={8}>
          <Form.Item label={translations[language].page2}>
            <Select
              value={output.page2}
              onChange={(value) => handleChange(index, 'page2', value, setOutputs, outputs)}
              style={{ width: '100%' }}
            >
              {pages.map((page) => (
                <Select.Option key={page.no} value={page.no}>
                  {`Sayfa ${page.no} ${page.name ? `- ${page.name}` : ''}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={translations[language].column2}>
            <Select
              value={output.column2}
              onChange={(value) => handleChange(index, 'column2', value, setOutputs, outputs)}
              style={{ width: '100%' }}
            >
              {[1, 2, 3, 4, 5].map((val) => (
                <Select.Option key={val} value={val}>
                  {val}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={translations[language].row2}>
            <Select
              value={output.row2}
              onChange={(value) => handleChange(index, 'row2', value, setOutputs, outputs)}
              style={{ width: '100%' }}
            >
              <Select.Option value={1}>1</Select.Option>
              <Select.Option value={2}>2</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
    )}
                      <Row gutter={16}>
                      <Col span={8}>
  <Form.Item 
    label={translations[language].currentGreenLimit}
    name={`currentgreenlimit_${index}`}
    rules={[
      {
        validator: (_, value) => {
          if (value < 0) {
            return Promise.reject(new Error('Negatif değer giremezsiniz'));
          }
          return Promise.resolve();
        }
      }
    ]}
    initialValue={100}  // Form için başlangıç değeri
  >
    <InputNumber
      min={0}
      defaultValue={100}  // Input için başlangıç değeri
      value={output.currentgreenlimit ?? 100}  // Eğer undefined/null ise 100 olacak
      onChange={(value) => handleChange(index, 'currentgreenlimit', value, setOutputs, outputs)}
      style={{ width: '100%' }}
    />
  </Form.Item>
</Col>
                        <Col span={8}>
                        <Form.Item label={translations[language].icon}>
                        <Button
                                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => {
                                  setCurrentOutputIndex(index);
                                  setIconModalVisible(true);
                                }}
                              >
                                {/* <img
                                  src={icons[output.icon]}
                                  alt={`icon ${output.icon}`}
                                  style={{ width: '24px', height: '24px', marginRight: '8px' }}
                                /> */}  
                                {output.icon}
                              </Button>
                      </Form.Item>
                      </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item label={translations[language].nameTR}>
                            <Input
                              value={output.nameTR}
                              onChange={(e) => handleInputChange(index, 'nameTR', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label={translations[language].nameGB}>
                            <Input
                              value={output.nameGB}
                              onChange={(e) => handleInputChange(index, 'nameGB', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label={translations[language].nameDE}>
                            <Input
                              value={output.nameDE}
                              onChange={(e) => handleInputChange(index, 'nameDE', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={handleAddOutput} style={{ marginBottom: '20px' }}>
                  {translations[language].addOutput}
                  </Button>
                  
                </Collapse.Panel>

                  {/* Inputs Section */}
                  <Collapse.Panel header={`${translations[language].inputs}`} key="3">
                  {inputs.map((input, index) => (
                    <Card
                      key={index}
                      title={`${translations[language].inputName} ${index + 1}`}
                      bordered
                      style={{ marginBottom: '20px', borderRadius: '8px' }}
                      extra={
                        <Popconfirm
                          title={translations[language].deleteConfirm}
                          onConfirm={() => handleDeleteInput(index)}
                          okText={translations[language].yes}
                          cancelText={translations[language].no}
                        >
                          <Button 
                            type="link" 
                            danger
                            style={{ fontWeight: 'bold', color: '#ff4d4f', padding: '0 10px' }}
                          >
                            {translations[language].delete}
                          </Button>
                        </Popconfirm>
                      }
                    >
                      <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item label={translations[language].inputPin}>
                        <Select
                          value={input.socketpin}
                          onChange={(value) => handleChange(index, 'socketpin', value, setInputs, inputs)}
                        >
                          {['B4', 'B3', 'B2', 'B1'].map((pin) => (
                            <Select.Option 
                              key={pin} 
                              value={pin} 
                              disabled={inputs.some((input) => input.socketpin === pin)}
                            >
                              {pin}
                            </Select.Option>
                          ))}
                        </Select>

                        </Form.Item>
                      </Col>
                      <Col span={8}>
                      <Form.Item label={translations[language].enable}>
                        <Select
                          value={input.enable}
                          onChange={(value) => handleChange(index, 'enable', value, setInputs, inputs)}
                          style={{ width: '100%' }}
                        >
                          <Select.Option value={"true"}>{translations[language].yes}</Select.Option>
                          <Select.Option value={"false"}>{translations[language].no}</Select.Option>
                        </Select>
                      </Form.Item>
                      </Col>
                        <Col span={8}>
                          <Form.Item label={translations[language].vavienOutput}>
                          <Select
                            value={input.vavienoutput}
                            onChange={(value) => handleChange(index, 'vavienoutput', value, setInputs, inputs)}
                            style={{ width: '100%' }}
                          >
                            {['A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'B1', 'B8', 'C6', 'C7', 'C8', 'D0', 'D1', 'D2'].map((pin) => (
                              <Select.Option 
                                key={pin} 
                                value={pin} 
                                disabled={inputs.some((input) => input.vavienoutput === pin)}
                              >
                                {pin}
                              </Select.Option>
                            ))}
                          </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                      <Col span={8}>
                      <Form.Item label={translations[language].page}>
      <Select
        value={input.page}
        onChange={(value) => handleChange(index, 'page', value, setInputs, inputs)}
        style={{ width: '100%' }}
      >
        {pages.map((page) => (
          <Select.Option key={page.no} value={page.no}>
            {`${translations[language].page} ${page.no} ${page.name ? `- ${page.name}` : ''}`}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
                        </Col>
                      <Col span={8}>
                        <Form.Item label={translations[language].column}>
                          <Select
                            value={input.column}
                            onChange={(value) => handleChange(index, 'column', value, setInputs, inputs)}
                            style={{ width: '100%' }}
                          >
                            {[0,1, 2, 3, 4, 5].map((value) => (
                              <Select.Option key={value} value={value}>
                                {value}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                        
                        <Col span={8}>
                          <Form.Item label={translations[language].row}>
                            <Select
                              value={input.row}
                              onChange={(value) => handleChange(index, 'row', value, setInputs, inputs)}
                              style={{ width: '100%' }}
                            >
                              {[0,1, 2].map((value) => (
                                <Select.Option key={value} value={value}>
                                  {value}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        {/* Klonla butonu: onClick ekleyerek clone işlemi başlatılıyor */}
      {/* <Col span={24}>
      {input.cloneActive ? (
          <Button type="default" style={{ width: '100%' }} onClick={() => handleRemoveCloneInput(index)}>
            {translations[language].deleteClone}
          </Button>
        ) : (
          <Button type="default" style={{ width: '100%' }} onClick={() => handleCloneInput(index)}>
            {translations[language].clone}
          </Button>
        )}
      </Col> */}
                      </Row>

                      {input.cloneActive && (
      <Row gutter={16} style={{ marginTop: '10px' }}>
        <Col span={8}>
          <Form.Item label={translations[language].page2}>
            <Select
              value={input.page2}
              onChange={(value) => handleChange(index, 'page2', value, setInputs, inputs)}
              style={{ width: '100%' }}
            >
              {pages.map((page) => (
                <Select.Option key={page.no} value={page.no}>
                  {`Sayfa ${page.no} ${page.name ? `- ${page.name}` : ''}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={translations[language].column2}>
            <Select
              value={input.column2}
              onChange={(value) => handleChange(index, 'column2', value, setInputs, inputs)}
              style={{ width: '100%' }}
            >
              {[1, 2, 3, 4, 5].map((val) => (
                <Select.Option key={val} value={val}>
                  {val}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={translations[language].row2}>
            <Select
              value={input.row2}
              onChange={(value) => handleChange(index, 'row2', value, setInputs, inputs)}
              style={{ width: '100%' }}
            >
              <Select.Option value={1}>1</Select.Option>
              <Select.Option value={2}>2</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
    )}
                      <Row gutter={16}>
                        <Col span={8}>
                        <Form.Item label={translations[language].icon}>
                        <Button
                                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => {
                                  setCurrentInputIndex(index);
                                  setIconModalVisible(true);
                                }}
                              >
                                {/* <img
                                  src={icons[output.icon]}
                                  alt={`icon ${output.icon}`}
                                  style={{ width: '24px', height: '24px', marginRight: '8px' }}
                                /> */}  
                                {input.icon}
                              </Button>
                      </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item label={translations[language].nameTR}>
                            <Input
                              value={input.nameTR}
                              onChange={(e) => handleInputChangeForInput(index, 'nameTR', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label={translations[language].nameGB}>
                            <Input
                              value={input.nameGB}
                              onChange={(e) => handleInputChangeForInput(index, 'nameGB', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label={translations[language].nameDE}>
                            <Input
                              value={input.nameDE}
                              onChange={(e) => handleInputChangeForInput(index, 'nameDE', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={handleAddInput} style={{ marginBottom: '20px' }}>
                  {translations[language].addInput}
                  </Button>
                </Collapse.Panel>
                
                  {/* Voltage Section */}
                  <Collapse.Panel header={`${translations[language].voltages}`} key="4">
                  {voltages.map((voltage, index) => (
                    <Card
                      key={index}
                      title={`${translations[language].voltageName} ${index + 1}`}
                      bordered
                      style={{ marginBottom: '20px', borderRadius: '8px' }}
                      extra={
                        <Popconfirm
                          title={translations[language].deleteConfirm}
                          onConfirm={() => handleDeleteVoltages(index)}
                          okText={translations[language].yes}
                          cancelText={translations[language].no}
                        >
                          <Button 
                            type="link" 
                            danger
                            style={{ fontWeight: 'bold', color: '#ff4d4f', padding: '0 10px' }}
                          >
                            {translations[language].delete}
                          </Button>
                        </Popconfirm>
                      }
                    >
                      <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item label={translations[language].voltagesPin}>
                        <Select
                          value={voltage.socketpin}
                          onChange={(value) => handleChange(index, 'socketpin', value, setVoltages, voltages)}
                          style={{ width: '100%' }}
                        >
                          {['00', 'C1', 'C2', 'C3', 'C4'].map((pin) => (
                            <Select.Option 
                              key={pin} 
                              value={pin} 
                              disabled={voltages.some((voltage) => voltage.socketpin === pin)}
                            >
                              {pin}
                            </Select.Option>
                          ))}
                        </Select>

                        </Form.Item>
                      </Col>
                      <Col span={8}>
                      <Form.Item label={translations[language].enable}>
                        <Select
                          value={voltage.enable}
                          onChange={(value) => handleChange(index, 'enable', value, setVoltages, voltages)}
                          style={{ width: '100%' }}
                        >
                          <Select.Option value={"true"}>{translations[language].yes}</Select.Option>
                          <Select.Option value={"false"}>{translations[language].no}</Select.Option>
                        </Select>
                      </Form.Item>

                      </Col>
                      </Row>
                      <Row gutter={16}>
                      <Col span={8}>
                      <Form.Item label={translations[language].page}>
      <Select
        value={voltage.page}
        onChange={(value) => handleChange(index, 'page', value, setVoltages, voltages)}
        style={{ width: '100%' }}
      >
        {pages.map((page) => (
          <Select.Option key={page.no} value={page.no}>
            {`${translations[language].page} ${page.no} ${page.name ? `- ${page.name}` : ''}`}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
                        </Col>
                      <Col span={8}>
                      <Form.Item label={translations[language].column}>
                        <Select
                          value={voltage.column}
                          onChange={(value) => handleChange(index, 'column', value, setVoltages, voltages)}
                          style={{ width: '100%' }}
                        >
                          {[0,1, 2, 3, 4, 5].map((value) => (
                            <Select.Option key={value} value={value}>
                              {value}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                        <Col span={8}>
                        <Form.Item label={translations[language].row}>
                          <Select
                            value={voltage.row}
                            onChange={(value) => handleChange(index, 'row', value, setVoltages, voltages)}
                            style={{ width: '100%' }}
                          >
                            {[0,1, 2].map((value) => (
                              <Select.Option key={value} value={value}>
                                {value}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={24}>
      {voltage.cloneActive ? (
          <Button type="default" style={{ width: '100%' }} onClick={() => handleRemoveCloneVoltage(index)}>
            {translations[language].deleteClone}
          </Button>
        ) : (
          <Button type="default" style={{ width: '100%' }} onClick={() => handleCloneVoltage(index)}>
            {translations[language].clone}
          </Button>
        )}
      </Col>
                      </Row>
                      {voltage.cloneActive && (
      <Row gutter={16} style={{ marginTop: '10px' }}>
        <Col span={8}>
          <Form.Item label={translations[language].page2}>
            <Select
              value={voltage.page2}
              onChange={(value) => handleChange(index, 'page2', value, setVoltages, voltages)}
              style={{ width: '100%' }}
            >
              {pages.map((page) => (
                <Select.Option key={page.no} value={page.no}>
                  {`Sayfa ${page.no} ${page.name ? `- ${page.name}` : ''}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={translations[language].column2}>
            <Select
              value={voltage.column2}
              onChange={(value) => handleChange(index, 'column2', value, setVoltages, voltages)}
              style={{ width: '100%' }}
            >
              {[1, 2, 3, 4, 5].map((val) => (
                <Select.Option key={val} value={val}>
                  {val}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={translations[language].row2}>
            <Select
              value={voltage.row2}
              onChange={(value) => handleChange(index, 'row2', value, setVoltages, voltages)}
              style={{ width: '100%' }}
            >
              <Select.Option value={1}>1</Select.Option>
              <Select.Option value={2}>2</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
    )}

                      <Row gutter={16}>
                      
                        <Col span={8}>
                        <Form.Item label={translations[language].icon}>
                        <Button
                                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => {
                                  setCurrentVoltageIndex(index);
                                  setIconModalVisible(true);
                                }}
                              >
                                {/* <img
                                  src={icons[output.icon]}
                                  alt={`icon ${output.icon}`}
                                  style={{ width: '24px', height: '24px', marginRight: '8px' }}
                                /> */}  
                                {voltage.icon}
                              </Button>
                      </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item label={translations[language].nameTR}>
                            <Input
                              value={voltage.nameTR}
                              onChange={(e) => handleVoltageChangeForInput(index, 'nameTR', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label={translations[language].nameGB}>
                            <Input
                              value={voltage.nameGB}
                              onChange={(e) => handleVoltageChangeForInput(index, 'nameGB', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label={translations[language].nameDE}>
                            <Input
                              value={voltage.nameDE}
                              onChange={(e) => handleVoltageChangeForInput(index, 'nameDE', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={handleAddVoltage} style={{ marginBottom: '20px' }}>
                  {translations[language].addVoltage}
                  </Button>
                  </Collapse.Panel>

                  {/* floats Section */}
                  <Collapse.Panel header={`${translations[language].floats}`} key="5">
                  {floats.map((float, index) => (
                    <Card
                      key={index}
                      title={`${translations[language].floatName} ${index + 1}`}
                      bordered
                      style={{ marginBottom: '20px', borderRadius: '8px' }}
                      extra={
                        <Popconfirm
                          title={translations[language].deleteConfirm}
                          onConfirm={() => handleDeleteFloat(index)}
                          okText={translations[language].yes}
                          cancelText={translations[language].no}
                        >
                          <Button 
                            type="link" 
                            danger
                            style={{ fontWeight: 'bold', color: '#ff4d4f', padding: '0 10px' }}
                          >
                            {translations[language].delete}
                          </Button>
                        </Popconfirm>
                      }
                    >
                      <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item label={translations[language].floatsPin}>
                        <Select
                          value={float.socketpin}
                          onChange={(value) => handleChange(index, 'socketpin', value, setFloats, floats)}
                          style={{ width: '100%' }}
                        >
                          {['B6', 'B7', 'C1', 'C2', 'C3'].map((pin) => (
                            <Select.Option
                              key={pin}
                              value={pin}
                              disabled={floats.some((float) => float.socketpin === pin)}
                            >
                              {pin}
                            </Select.Option>
                          ))}
                        </Select>

                        </Form.Item>
                      </Col>
                      <Col span={8}>
                      <Form.Item label={translations[language].enable}>
                        <Select
                          value={float.enable}
                          onChange={(value) => handleChange(index, 'enable', value, setFloats, floats)}
                          style={{ width: '100%' }}
                        >
                          <Select.Option value={"true"}>{translations[language].yes}</Select.Option>
                          <Select.Option value={"false"}>{translations[language].no}</Select.Option>
                        </Select>
                      </Form.Item>

                      </Col>
                      <Col span={8}>
                        <Form.Item label={translations[language].type}>
                          <Select
                            value={float.type}
                            onChange={(value) => handleChange(index, 'type', value, setFloats, floats)}
                            style={{ width: '100%' }}
                          >
                            <Select.Option value="0-190">0-190</Select.Option>
                            <Select.Option value="240-33">240-33</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label={translations[language].purpose}>
                          <Select
                            value={float.purpose}
                            onChange={(value) => handleChange(index, 'purpose', value, setFloats, floats)}
                            style={{ width: '100%' }}
                          >
                            <Select.Option value="water">{translations[language].water}</Select.Option>
                            <Select.Option value="drain">{translations[language].drain}</Select.Option>
                            <Select.Option value="gray">{translations[language].gray}</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
  <Form.Item 
    label={translations[language].warnPercent}
    name={`warn_percent_${index}`}
    rules={[
      {
        validator: (_, value) => {
          if (value < 0) {
            return Promise.reject(new Error('Negatif değer giremezsiniz'));
          }
          return Promise.resolve();
        }
      }
    ]}
    initialValue={20}  // Form için başlangıç değeri
  >
    <InputNumber
      min={0}
      defaultValue={20}  // Input için başlangıç değeri
      value={float.warn_percent ?? 20}  // Eğer undefined/null ise 20 olacak
      onChange={(value) => handleChange(index, 'warn_percent', value, setFloats, floats)}
      style={{ width: '100%' }}
    />
  </Form.Item>
</Col>
<Col span={8}>
  <Form.Item 
    label={translations[language].errorPercent}
    name={`error_percent_${index}`}
    rules={[
      {
        validator: (_, value) => {
          if (value < 0) {
            return Promise.reject(new Error('Negatif değer giremezsiniz'));
          }
          return Promise.resolve();
        }
      }
    ]}
    initialValue={0}  // Form için başlangıç değeri (0 olarak ayarlandı)
  >
    <InputNumber
      min={0}
      defaultValue={0}  // Input için başlangıç değeri (0 olarak ayarlandı)
      value={float.error_percent ?? 0}  // Eğer undefined/null ise 0 olacak
      onChange={(value) => handleChange(index, 'error_percent', value, setFloats, floats)}
      style={{ width: '100%' }}
    />
  </Form.Item>
</Col>
                        <Col span={8}>
                          <Form.Item label={translations[language].lock_pin}>
                          <Select
                              value={float.lock_pin}
                              onChange={(value) => handleChange(index, 'lock_pin', value, setFloats, floats)}
                              style={{ width: '100%' }}
                            >
                              {['A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'B1', 'B8', 'C6', 'C7', 'C8', 'D0', 'D1', 'D2', ''].map((pin) => (
                                <Select.Option
                                  key={pin}
                                  value={pin}
                                  disabled={floats.some((float) => float.lock_pin === pin && pin !== '')}
                                >
                                  {pin || 'Boş'}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>

                      <Col span={8}>
                      <Form.Item label={translations[language].page}>
      <Select
        value={float.page}
        onChange={(value) => handleChange(index, 'page', value, setFloats, floats)}
        style={{ width: '100%' }}
      >
        {pages.map((page) => (
          <Select.Option key={page.no} value={page.no}>
            {`${translations[language].page} ${page.no} ${page.name ? `- ${page.name}` : ''}`}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
                        </Col>
                      <Col span={8}>
                        <Form.Item label={translations[language].column}>
                          <Select
                            value={float.column}
                            onChange={(value) => handleChange(index, 'column', value, setFloats, floats)}
                            style={{ width: '100%' }}
                          >
                            {[0,1, 2, 3, 4, 5].map((value) => (
                              <Select.Option key={value} value={value}>
                                {value}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                        
                        <Col span={8}>
                        <Form.Item label={translations[language].row}>
                          <Select
                            value={float.row}
                            onChange={(value) => handleChange(index, 'row', value, setFloats, floats)}
                            style={{ width: '100%' }}
                          >
                            {[0,1, 2].map((value) => (
                              <Select.Option key={value} value={value}>
                                {value}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={24}>
      {float.cloneActive ? (
          <Button type="default" style={{ width: '100%' }} onClick={() => handleRemoveCloneFloat(index)}>
            {translations[language].deleteClone} 
          </Button>
        ) : (
          <Button type="default" style={{ width: '100%' }} onClick={() => handleCloneFloat(index)}>
            {translations[language].clone}
          </Button>
        )}
      </Col>
                      </Row>
                      {float.cloneActive && (
      <Row gutter={16} style={{ marginTop: '10px' }}>
        <Col span={8}>
          <Form.Item label={translations[language].page2}>
            <Select
              value={float.page2}
              onChange={(value) => handleChange(index, 'page2', value, setFloats, floats)}
              style={{ width: '100%' }}
            >
              {pages.map((page) => (
                <Select.Option key={page.no} value={page.no}>
                  {`Sayfa ${page.no} ${page.name ? `- ${page.name}` : ''}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={translations[language].column2}>
            <Select
              value={float.column2}
              onChange={(value) => handleChange(index, 'column2', value, setFloats, floats)}
              style={{ width: '100%' }}
            >
              {[1, 2, 3, 4, 5].map((val) => (
                <Select.Option key={val} value={val}>
                  {val}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={translations[language].row2}>
            <Select
              value={float.row2}
              onChange={(value) => handleChange(index, 'row2', value, setFloats, floats)}
              style={{ width: '100%' }}
            >
              <Select.Option value={1}>1</Select.Option>
              <Select.Option value={2}>2</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
    )}
                      <Row gutter={16}>
                        
                        <Col span={8}>
                        <Form.Item label={translations[language].icon}>
                        <Button
                                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => {
                                  setCurrentFloatIndex(index);
                                  setIconModalVisible(true);
                                }}
                              >
                                {/* <img
                                  src={icons[output.icon]}
                                  alt={`icon ${output.icon}`}
                                  style={{ width: '24px', height: '24px', marginRight: '8px' }}
                                /> */}  
                                {float.icon}
                              </Button>
                      </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item label={translations[language].nameTR}>
                            <Input
                              value={float.nameTR}
                              onChange={(e) => handleVoltageChangeForFloat(index, 'nameTR', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label={translations[language].nameGB}>
                            <Input
                              value={float.nameGB}
                              onChange={(e) => handleVoltageChangeForFloat(index, 'nameGB', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label={translations[language].nameDE}>
                            <Input
                              value={float.nameDE}
                              onChange={(e) => handleVoltageChangeForFloat(index, 'nameDE', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={handleAddFloat} style={{ marginBottom: '20px' }}>
                  {translations[language].addFloat}
                  </Button>
                  </Collapse.Panel>

                  {/* relay Section */}
                  <Collapse.Panel header={`${translations[language].relay}`} key="6">
                  {relays.map((relay, index) => (
                    <Card
                      key={index}
                      title={`${translations[language].relay}`}
                      bordered
                      style={{ marginBottom: '20px', borderRadius: '8px' }}
                      extra={
                        <Popconfirm
                          title={translations[language].deleteConfirm}
                          onConfirm={() => handleDeleteFloat(index)}
                          okText={translations[language].yes}
                          cancelText={translations[language].yes}
                        >
                        </Popconfirm>
                      }
                    >
                      <Row gutter={16}>
                      <Col span={8}>
                      <Form.Item label={translations[language].active}>
                        <Select
                          value={relay.enable}
                          onChange={(value) => handleChange(index, 'enable', value, setRelays, relays)}
                          style={{ width: '100%' }}
                        >
                          <Select.Option value={"true"}>{translations[language].yes}</Select.Option>
                          <Select.Option value={"false"}>{translations[language].no}</Select.Option>
                        </Select>
                      </Form.Item>

                      </Col>
                      <Col span={8}>
                        <Form.Item label={translations[language].shortCircuitSocketpin}>
                          <Select
                            value={relay.short_circuit_socketpin}
                            onChange={(value) => handleChange(index, 'short_circuit_socketpin', value, setRelays, relays)}
                            style={{ width: '100%' }}
                          >
                            <Select.Option value="A1">A1</Select.Option>
                            <Select.Option value="A2">A2</Select.Option>
                            <Select.Option value="A3">A3</Select.Option>
                            <Select.Option value="A4">A4</Select.Option>
                            <Select.Option value="A5">A5</Select.Option>
                            <Select.Option value="A6">A6</Select.Option>
                            <Select.Option value="A7">A7</Select.Option>
                            <Select.Option value="A8">A8</Select.Option>
                            <Select.Option value="B1">B1</Select.Option>
                            <Select.Option value="B8">B8</Select.Option>
                            <Select.Option value="C6">C6</Select.Option>
                            <Select.Option value="C7">C7</Select.Option>
                            <Select.Option value="C8">C8</Select.Option>
                            <Select.Option value="D0">D0</Select.Option>
                            <Select.Option value="D1">D1</Select.Option>
                            <Select.Option value="D2">D2</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                          <Form.Item label={translations[language].openCircuitSocketpin}>
                            <Select
                              value={relay.open_circuit_socketpin}
                              onChange={(value) => handleChange(index, 'open_circuit_socketpin', value, setRelays, relays)}
                              style={{ width: '100%' }}
                            >
                              <Select.Option value="A1">A1</Select.Option>
                              <Select.Option value="A2">A2</Select.Option>
                              <Select.Option value="A3">A3</Select.Option>
                              <Select.Option value="A4">A4</Select.Option>
                              <Select.Option value="A5">A5</Select.Option>
                              <Select.Option value="A6">A6</Select.Option>
                              <Select.Option value="A7">A7</Select.Option>
                              <Select.Option value="A8">A8</Select.Option>
                              <Select.Option value="B1">B1</Select.Option>
                              <Select.Option value="B8">B8</Select.Option>
                              <Select.Option value="C6">C6</Select.Option>
                              <Select.Option value="C7">C7</Select.Option>
                              <Select.Option value="C8">C8</Select.Option>
                              <Select.Option value="D0">D0</Select.Option>
                              <Select.Option value="D1">D1</Select.Option>
                              <Select.Option value="D2">D2</Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={8}>
  <Form.Item 
    label={translations[language].shortCircuitVoltage}
    name={`short_circuit_voltage_${index}`}
    rules={[
      {
        validator: (_, value) => {
          if (value < 0) {
            return Promise.reject(new Error('13200\'den küçük değer giremezsiniz'));
          }
          return Promise.resolve();
        }
      }
    ]}
    initialValue={13200}
  >
    <InputNumber
      min={13200}
      defaultValue={13200}
      value={relay.short_circuit_voltage ?? 13200}
      onChange={(value) => handleChange(index, 'short_circuit_voltage', value, setRelays, relays)}
      style={{ width: '100%' }}
    />
  </Form.Item>
</Col>

<Col span={8}>
  <Form.Item 
    label={translations[language].openCircuitVoltage}
    name={`open_circuit_voltage_${index}`}
    rules={[
      {
        validator: (_, value) => {
          if (value < 0) {
            return Promise.reject(new Error('13200\'den küçük değer giremezsiniz'));
          }
          return Promise.resolve();
        }
      }
    ]}
    initialValue={12500}
  >
    <InputNumber
      min={12500}
      defaultValue={12500}
      value={relay.open_circuit_voltage ?? 12500}
      onChange={(value) => handleChange(index, 'open_circuit_voltage', value, setRelays, relays)}
      style={{ width: '100%' }}
    />
  </Form.Item>
</Col>
<Col span={8}>
  <Form.Item label={translations[language].manualOverrideDuration}>
    <Input
      type="number"  // Sadece sayısal girişe izin verir
      min="0"       // Minimum değeri 0 olarak ayarlar (HTML5 özelliği)
      value={relay.manualoverride_duration}
      onChange={(e) => {
        const value = e.target.value;
        // Negatif değer girilirse engelle (veya sıfıra eşitle)
        if (value >= 0) {
          handleChange(index, 'manualoverride_duration', value, setRelays, relays);
        }
      }}
    />
  </Form.Item>
</Col>
                      </Row>
                      
                    </Card>
                  ))}

                  </Collapse.Panel>

                  {/* setting Section */}
                  <Collapse.Panel header={`${translations[language].setting}`} key="7">
                  {settings.map((setting, index) => (
                    <Card
                      key={index}
                      title={`${translations[language].setting}`}
                      bordered
                      style={{ marginBottom: '20px', borderRadius: '8px' }}
                      extra={
                        <Popconfirm
                          title={translations[language].deleteConfirm}
                          onConfirm={() => handleDeleteFloat(index)}
                          okText={translations[language].yes}
                          cancelText={translations[language].no}
                        >
                        </Popconfirm>
                      }
                    >
                      <Row gutter={16}>
  <Col span={8}>
    <Form.Item
      label={translations[language].forceShutDownVoltage}
      name={`force_shut_down_voltage_${index}`}
      rules={[
        {
          validator: (_, value) =>
            value < 0 ? Promise.reject(new Error('Negatif değer giremezsiniz')) : Promise.resolve(),
        },
      ]}
      initialValue={10500}
    >
      <InputNumber
        min={0}
        defaultValue={10500}
        value={setting.force_shut_down_voltage ?? 10500}
        onChange={(value) => handleChange(index, 'force_shut_down_voltage', value, setSettings, settings)}
        style={{ width: '100%' }}
      />
    </Form.Item>
  </Col>

  <Col span={8}>
    <Form.Item
      label={translations[language].optionalDownVoltage}
      name={`optional_down_voltage_${index}`}
      rules={[
        {
          validator: (_, value) =>
            value < 0 ? Promise.reject(new Error('Negatif değer giremezsiniz')) : Promise.resolve(),
        },
      ]}
      initialValue={11000}
    >
      <InputNumber
        min={0}
        defaultValue={11000}
        value={setting.optional_down_voltage ?? 11000}
        onChange={(value) => handleChange(index, 'optional_down_voltage', value, setSettings, settings)}
        style={{ width: '100%' }}
      />
    </Form.Item>
  </Col>

  <Col span={8}>
    <Form.Item
      label={translations[language].startWarnVoltage}
      name={`start_warn_voltage_${index}`}
      rules={[
        {
          validator: (_, value) =>
            value < 0 ? Promise.reject(new Error('Negatif değer giremezsiniz')) : Promise.resolve(),
        },
      ]}
      initialValue={11500}
    >
      <InputNumber
        min={0}
        defaultValue={11500}
        value={setting.start_warn_voltage ?? 11500}
        onChange={(value) => handleChange(index, 'start_warn_voltage', value, setSettings, settings)}
        style={{ width: '100%' }}
      />
    </Form.Item>
  </Col>

  <Col span={8}>
    <Form.Item
      label={translations[language].recoverVoltage}
      name={`recover_voltage_${index}`}
      rules={[
        {
          validator: (_, value) =>
            value < 0 ? Promise.reject(new Error('Negatif değer giremezsiniz')) : Promise.resolve(),
        },
      ]}
      initialValue={12000}
    >
      <InputNumber
        min={0}
        defaultValue={12000}
        value={setting.recover_voltage ?? 12000}
        onChange={(value) => handleChange(index, 'recover_voltage', value, setSettings, settings)}
        style={{ width: '100%' }}
      />
    </Form.Item>
  </Col>

  <Col span={8}>
    <Form.Item
      label={translations[language].forceShutDownTime}
      name={`force_shut_down_time_${index}`}
      rules={[
        {
          validator: (_, value) =>
            value < 0 ? Promise.reject(new Error('Negatif değer giremezsiniz')) : Promise.resolve(),
        },
      ]}
      initialValue={60}
    >
      <InputNumber
        min={0}
        defaultValue={60}
        value={setting.force_shut_down_time ?? 60}
        onChange={(value) => handleChange(index, 'force_shut_down_time', value, setSettings, settings)}
        style={{ width: '100%' }}
      />
    </Form.Item>
  </Col>
</Row>

                      
                    </Card>
                  ))}
                  </Collapse.Panel>
              </Collapse>
            </Form>
            <Space style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
        <Button type="primary" onClick={openCustomerModal}>
          {/* translations[language].dkvi ifadesi */}
          {translations[language].dkvi}
        </Button>
      </Space>
          </Card>

          <Modal
        title={translations[language].customerInformation}
        visible={isModalVisible}
        footer={null}
        onCancel={handleModalCancel}
      >
        <Form
          layout="vertical"
          initialValues={{
            companyName: '',
            caravanType: '',
            description: '',
            customerNameSurname: '',
            customerPhone: '',
            customerCountry: ''
          }}
          onFinish={handleModalOk}
        >
          <Form.Item label={translations[language].companyName} name="companyName">
            <Input placeholder={translations[language].optional}/>
          </Form.Item>
          <Form.Item label={translations[language].caravanType} name="caravanType">
            <Input placeholder={translations[language].optional} />
          </Form.Item>
          <Form.Item label={translations[language].description} name="description">
            <Input.TextArea placeholder={translations[language].optional} />
          </Form.Item>
          <Form.Item label={translations[language].customerNameSurname} name="customerNameSurname">
            <Input placeholder={translations[language].optional} />
          </Form.Item>
          <Form.Item label={translations[language].customerPhone} name="customerPhone">
            <Input placeholder={translations[language].optional} />
          </Form.Item>
          <Form.Item label={translations[language].customerCountry} name="customerCountry">
            <Input placeholder={translations[language].optional} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
            {translations[language].dkvi}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
        </div>
        </div>
        <Modal
          title={translations[language].selectIcon}
          visible={iconModalVisible}
          onCancel={() => setIconModalVisible(false)}
          footer={null}
          bodyStyle={{ backgroundColor: '#1890ff', padding: '30px', minHeight: '300px', borderRadius: '10px' }}
          width={600} 
          
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'center' }}>
            {[...Array(100)].map((_, i) => (
              <div
                key={i}
                onClick={() => handleIconSelect(i)}
                style={{
                  cursor: 'pointer',
                  padding: '10px',
                  border: '1px solid #fff',
                  borderRadius: '10px',
                }}
              >
                <img src={icons[i]} alt={`icon ${i}`} style={{ width: '40px', height: '40px' }} />
              </div>
            ))}
          </div>
        </Modal>
        </ConfigProvider>
    </AppLayout>
    
  );
};
export default EditableJson;